import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import { Calculator, SlidersHorizontal, Lock, FlaskConical } from "lucide-react";

const TIER_META = {
  public: { label: "Public Marketplace", accent: "border-taven-gold" },
  dropship: { label: "Dropshipping Portal", accent: "border-taven-orange" },
  wholesale: { label: "Wholesale Portal", accent: "border-taven-black" },
};

const NUM_FIELDS = [
  ["target_net_profit", "Target Net Profit ($)"],
  ["payment_fee_pct", "Payment Fee (%)"],
  ["fixed_transaction_fee", "Fixed Txn Fee ($)"],
  ["shipping_reserve", "Shipping Reserve ($)"],
  ["refund_reserve_pct", "Return/Refund Reserve (%)"],
  ["commission_reserve_pct", "Affiliate/Creator Commission (%)"],
  ["seller_commission_pct", "Seller Commission (%)"],
  ["platform_overhead", "Platform Overhead ($)"],
  ["tax_duty_reserve_pct", "Tax/Duty Reserve (%)"],
  ["min_price", "Minimum Allowed Price ($)"],
  ["max_market_price", "Max Market Price ($, 0 = none)"],
];

function RuleCard({ rule, onSaved }) {
  const [form, setForm] = useState(rule);
  const [saving, setSaving] = useState(false);
  const [sim, setSim] = useState(null);
  const [cost, setCost] = useState("15.00");
  const meta = TIER_META[rule.tier];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { reason: "admin rule edit" };
      NUM_FIELDS.forEach(([k]) => (payload[k] = Number(form[k]) || 0));
      payload.rounding = form.rounding;
      payload.simulation_mode = form.simulation_mode;
      payload.activation_approved = form.activation_approved;
      const { data } = await adminClient().put(`/admin/cj/pricing-rules/${rule.tier}`, payload);
      onSaved?.(data);
      toast.success(`${meta.label} rule saved (not activated)`);
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const simulate = async () => {
    try {
      const { data } = await adminClient().post(`/admin/cj/pricing-rules/${rule.tier}/simulate`, {
        product_cost: Number(cost) || 0,
      });
      setSim(data);
    } catch { toast.error("Simulation failed"); }
  };

  return (
    <div className={`border-l-4 ${meta.accent} border border-black/10 bg-white p-5`} data-testid={`pricing-rule-${rule.tier}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-black font-display">{meta.label}</h3>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-taven-gray text-muted-foreground flex items-center gap-1">
          <Lock className="w-3 h-3" /> {form.activation_approved ? "Approved" : "Not activated"}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {NUM_FIELDS.map(([k, label]) => (
          <label key={k} className="text-[11px] font-medium text-muted-foreground">
            {label}
            <input type="number" step="0.01" value={form[k] ?? 0}
              onChange={(e) => set(k, e.target.value)}
              className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm text-taven-black outline-none focus:border-taven-gold"
              data-testid={`rule-${rule.tier}-${k}`} />
          </label>
        ))}
        <label className="text-[11px] font-medium text-muted-foreground">
          Rounding Rule
          <select value={form.rounding} onChange={(e) => set("rounding", e.target.value)}
            className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold"
            data-testid={`rule-${rule.tier}-rounding`}>
            <option value="charm_99">Charm .99</option>
            <option value="nearest_50">Nearest .50</option>
            <option value="whole">Whole $</option>
            <option value="none">None</option>
          </select>
        </label>
        <div className="flex items-end gap-3">
          <label className="text-[11px] font-medium flex items-center gap-1.5 text-muted-foreground">
            <input type="checkbox" checked={!!form.simulation_mode} onChange={(e) => set("simulation_mode", e.target.checked)}
              data-testid={`rule-${rule.tier}-simulation`} /> Simulation mode
          </label>
          <label className="text-[11px] font-medium flex items-center gap-1.5 text-muted-foreground">
            <input type="checkbox" checked={!!form.activation_approved} onChange={(e) => set("activation_approved", e.target.checked)}
              data-testid={`rule-${rule.tier}-approved`} /> Activation approved
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <button onClick={save} disabled={saving}
          className="bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-50"
          data-testid={`rule-${rule.tier}-save`}>{saving ? "Saving…" : "Save Rule"}</button>
        <span className="text-[10px] text-muted-foreground">Config only — never applied to live prices in Phase A.</span>
      </div>

      <div className="mt-4 border-t border-black/10 pt-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
          <FlaskConical className="w-3.5 h-3.5" /> Price Simulation
        </p>
        <div className="flex items-center gap-2">
          <input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)}
            placeholder="Product cost" className="w-28 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold"
            data-testid={`sim-cost-${rule.tier}`} />
          <button onClick={simulate} className="bg-taven-gold text-taven-black px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:brightness-95"
            data-testid={`sim-run-${rule.tier}`}><Calculator className="w-3.5 h-3.5 inline mr-1" />Simulate</button>
        </div>
        {sim && (
          <div className="mt-2 text-xs grid grid-cols-3 gap-2" data-testid={`sim-result-${rule.tier}`}>
            <div><span className="text-lg font-black font-display">${sim.recommended_price}</span><p className="text-[10px] text-muted-foreground">Recommended</p></div>
            <div><span className="text-lg font-black font-display">${sim.estimated_net_profit}</span><p className="text-[10px] text-muted-foreground">Net profit</p></div>
            <div><span className={`text-lg font-black font-display ${sim.meets_target ? "text-green-600" : "text-red-600"}`}>{sim.meets_target ? "✓" : "✗"}</span><p className="text-[10px] text-muted-foreground">Meets target</p></div>
            {sim.over_max_market_price && <p className="col-span-3 text-[10px] text-amber-700">⚠ Exceeds max market price ceiling</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPricingRules() {
  const [rules, setRules] = useState(null);
  useEffect(() => {
    adminClient().get("/admin/cj/pricing-rules").then(({ data }) => setRules(data.rules)).catch(() => setRules([]));
  }, []);
  if (!rules) return <p className="text-sm text-muted-foreground">Loading pricing rules…</p>;

  return (
    <div data-testid="admin-pricing-rules">
      <div className="flex items-center gap-2 mb-1"><SlidersHorizontal className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Pricing Rule Configuration</h1></div>
      <p className="text-sm text-muted-foreground mb-5">Three independent profit rules — one per environment. Configure and simulate freely. Rules are <strong>never applied to live prices</strong> until you approve activation in a separate step.</p>
      <div className="grid gap-5 lg:grid-cols-3">
        {rules.map((r) => (
          <RuleCard key={r.tier} rule={r} onSaved={(u) => setRules((rs) => rs.map((x) => (x.tier === u.tier ? u : x)))} />
        ))}
      </div>
    </div>
  );
}
